import {
  Grid,
  FormControl,
  FormLabel,
  Input,
  Button,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import DateField from 'components/fields/DateField';
import SelectSearch from 'components/fields/SelectSearch';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import { useAnswerCodes } from '../../hooks/useAnswerCodes';
import { useValidationSchema } from '../../hooks/useValidationSchema';
import ValidatedDateField from '../../components/ValidatedDateField';
import RenewalCarriersModal from './RenewalCarriersModal';
import type { DriverInsuranceDetailsProps } from './types';

const DriverInsuranceDetails = ({
  driver,
  index,
  effectiveDate,
  onNestedChange,
}: DriverInsuranceDetailsProps) => {
  const insuranceDetails = driver.insurance_details;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const renewalCarriers = insuranceDetails?.renewal_carriers ?? [];
  const {
    getOptions,
    getCode,
    isLoading: isLoadingAnswerCodes,
  } = useAnswerCodes();
  const { isFieldRequired } = useValidationSchema();

  const isApplicant = index === 0;
  const currentInsurerOptions = getOptions(
    isApplicant ? 'current_previous_insurer' : 'current_insurer',
  ).filter((opt) => opt.value !== '');

  const otherInsurerCode = getCode('Other', 'current_previous_insurer');
  const showOtherInsurer =
    isApplicant && insuranceDetails?.current_insurer === otherInsurerCode;
  const allInsurerOptions = getOptions('current_insurer').filter(
    (opt) => opt.value !== '',
  );

  const driverEntity = driver as unknown as Record<string, unknown>;
  const isRequired = (field: string) =>
    isFieldRequired('driver', `insurance_details.${field}`, driverEntity);

  const calculateYears = (date: string | null): number | null => {
    if (!date || !effectiveDate) return null;
    const effDate = new Date(effectiveDate);
    const startDate = new Date(date);
    return Math.floor(
      (effDate.getTime() - startDate.getTime()) /
        (365.25 * 24 * 60 * 60 * 1000),
    );
  };

  const contInsuredYears = calculateYears(
    insuranceDetails?.continuously_insured_date ?? null,
  );

  const handleSaveRenewalCarriers = (codes: string[]) => {
    onNestedChange(index, 'insurance_details', 'renewal_carriers', codes);
  };

  const getSelectedCarrierNames = () => {
    return renewalCarriers
      .map((code: string) => {
        const opt = allInsurerOptions.find((o) => o.value === code);
        return opt?.label;
      })
      .filter(Boolean)
      .join(', ');
  };

  return (
    <>
      <VStack spacing={2} align="stretch">
        {/* Cont. Insured Date / Years */}
        <Grid templateColumns="1fr 60px" gap={1}>
          <DateField
            label="Cont. Insured"
            date={parseDateString(
              insuranceDetails?.continuously_insured_date ?? null,
            )}
            onChange={(date: Date | null) =>
              onNestedChange(
                index,
                'insurance_details',
                'continuously_insured_date',
                formatDateToString(date),
              )
            }
            isDisabled={false}
            size="sm"
            required={isRequired('continuously_insured_date')}
          />
          <FormControl>
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              &nbsp;
            </FormLabel>
            <Input
              variant="main"
              size="sm"
              value={contInsuredYears !== null ? contInsuredYears : ''}
              isReadOnly
              bg="gray.50"
            />
          </FormControl>
        </Grid>

        {/* Company Insured + Renewal Button */}
        <Grid templateColumns="1fr auto" gap={1} alignItems="end">
          <DateField
            label={`Company Insured${isRequired('company_insured_date') ? ' *' : ''}`}
            date={parseDateString(
              insuranceDetails?.company_insured_date ?? null,
            )}
            onChange={(date: Date | null) =>
              onNestedChange(
                index,
                'insurance_details',
                'company_insured_date',
                formatDateToString(date),
              )
            }
            isDisabled={false}
            size="sm"
            required={isRequired('company_insured_date')}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={onOpen}
            isDisabled={false}
            colorScheme="blue"
            mb="1px"
          >
            Renewal
          </Button>
        </Grid>

        {/* Selected Renewal Carriers Display */}
        {renewalCarriers.length > 0 && (
          <Input
            variant="main"
            size="sm"
            value={getSelectedCarrierNames()}
            isReadOnly
            bg="gray.50"
            fontSize="xs"
          />
        )}

        {/* Current Carrier */}
        <FormControl
          isRequired={isRequired('current_insurer')}
          isInvalid={
            isRequired('current_insurer') && !insuranceDetails?.current_insurer
          }
        >
          <FormLabel fontSize="xs" whiteSpace="nowrap">
            Current Carrier
          </FormLabel>
          <SelectSearch
            options={currentInsurerOptions}
            value={insuranceDetails?.current_insurer ?? ''}
            onChange={(value) =>
              onNestedChange(
                index,
                'insurance_details',
                'current_insurer',
                value ?? '',
              )
            }
            isDisabled={false}
            isLoading={isLoadingAnswerCodes}
            isInvalid={
              isRequired('current_insurer') &&
              !insuranceDetails?.current_insurer
            }
            placeholder="Select..."
            allowClear
          />
        </FormControl>

        {/* Other Insurer Name (shown when "Other" is selected) */}
        {showOtherInsurer && (
          <FormControl>
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              Other Insurer Name
            </FormLabel>
            <Input
              variant="main"
              size="sm"
              value={insuranceDetails?.other_insurer ?? ''}
              onChange={(e) =>
                onNestedChange(
                  index,
                  'insurance_details',
                  'other_insurer',
                  e.target.value,
                )
              }
              isDisabled={false}
              placeholder="Enter insurer name..."
            />
          </FormControl>
        )}

        {/* Brokerage Insured */}
        <ValidatedDateField
          itemPath="driver"
          fieldPath="insurance_details.brokerage_insured_date"
          label="Brokerage Insured"
          date={parseDateString(
            insuranceDetails?.brokerage_insured_date ?? null,
          )}
          entity={driverEntity}
          onChange={(date: Date | null) =>
            onNestedChange(
              index,
              'insurance_details',
              'brokerage_insured_date',
              formatDateToString(date),
            )
          }
          isDisabled={false}
        />

        {/* Owner/Principal */}
        <DateField
          label={`Owner/Principal${isRequired('owner_principal_date') ? ' *' : ''}`}
          date={parseDateString(insuranceDetails?.owner_principal_date ?? null)}
          onChange={(date: Date | null) =>
            onNestedChange(
              index,
              'insurance_details',
              'owner_principal_date',
              formatDateToString(date),
            )
          }
          isDisabled={false}
          size="sm"
          required={isRequired('owner_principal_date')}
        />
      </VStack>

      <RenewalCarriersModal
        isOpen={isOpen}
        onClose={onClose}
        selectedCodes={renewalCarriers}
        onSave={handleSaveRenewalCarriers}
      />
    </>
  );
};

export default DriverInsuranceDetails;
